
import { User, UserConfig, PlanType, SystemSettings, EmailJsConfig, AnalyticsEvent, Appointment, GlobalPricing, BillingProfile, Payment, UserProfile, DocumentType, Document, DocumentNote, CostCenter, Supplier, Department, AssetCategory, Manufacturer, Asset, AssetHistoryEvent } from '../types';
import { auth, db, firebaseConfig, app } from './firebase';
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import { getFirestore, collection, query, where, getCountFromServer, orderBy, getDocs } from "firebase/firestore";

export const api = {

  login: async (email: string, password: string): Promise<User> => {
    try {
      const userCredential = await auth.signInWithEmailAndPassword(email, password);
      const uid = userCredential.user!.uid;

      const userDocRef = db.collection("users").doc(uid);
      const userDocSnap = await userDocRef.get();

      if (!userDocSnap.exists) {
        const newUser = {
          email: email,
          role: email.includes('admin') ? 'admin' : 'user',
          plan: PlanType.FREE,
          subscriptionStatus: 'active',
          subscriptionEnd: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        };
        await userDocRef.set(newUser);
        return { id: uid, ...newUser, config: getDefaultConfig(email) } as User;
      }

      const userData = userDocSnap.data();

      if (userData?.subscriptionStatus === 'inactive') {
        await auth.signOut();
        throw new Error("Your account is inactive. Please contact your agency.");
      }

      const configDocRef = db.collection("configs").doc(uid);
      const configDocSnap = await configDocRef.get();
      const configData = configDocSnap.exists ? configDocSnap.data() : getDefaultConfig(email);

      // Handle legacy roles if needed, or default
      return {
        id: uid,
        ...userData,
        role: userData?.role || 'user',
        config: configData
      } as User;
    } catch (error: any) {
      console.error("Login Error:", error);
      throw new Error(error.message);
    }
  },

  loginWithGoogle: async (): Promise<User> => {
    try {
      const provider = new firebase.auth.GoogleAuthProvider();
      const result = await auth.signInWithPopup(provider);
      const idToken = await result.user!.getIdToken(true);

      // Verify with backend and get full user data
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: idToken })
      });

      if (!response.ok) {
        throw new Error('Google Authentication Failed on Server');
      }

      const userData = await response.json();
      return userData as User;

    } catch (error: any) {
      console.error("Google Login Error:", error);
      throw new Error(error.message);
    }
  },

  getUser: async (userId: string): Promise<User> => {
    const doc = await db.collection("users").doc(userId).get();
    if (!doc.exists) throw new Error("User not found");
    const data = doc.data();
    const configSnap = await db.collection("configs").doc(userId).get();
    const configData = configSnap.exists ? configSnap.data() : getDefaultConfig(data?.email);
    return { id: userId, ...data, config: configData } as User;
  },

  register: async (email: string, password: string): Promise<User> => {
    try {
      const userCredential = await auth.createUserWithEmailAndPassword(email, password);
      const uid = userCredential.user!.uid;

      const role = email.includes('admin') ? 'admin' : 'user';

      const newUser = {
        email: email,
        role: role,
        plan: PlanType.FREE,
        subscriptionStatus: 'active',
        subscriptionEnd: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      };

      await db.collection("users").doc(uid).set(newUser);

      const defaultConfig = getDefaultConfig(email);
      await db.collection("configs").doc(uid).set(defaultConfig);

      return {
        id: uid,
        ...newUser,
        config: defaultConfig
      } as User;

    } catch (error: any) {
      console.error("Register Error:", error);
      throw new Error(error.message);
    }
  },

  sendPasswordResetLink: async (email: string): Promise<void> => {
    try {
      await auth.sendPasswordResetEmail(email);
    } catch (error: any) {
      console.error("Reset Error:", error);
      let message = error.message;
      if (error.code === 'auth/user-not-found') {
        message = "No account found with this email in Firebase Authentication. Please check the email or register.";
      }
      throw new Error(message);
    }
  },

  updateUserPassword: async (newPassword: string): Promise<void> => {
    if (auth.currentUser) {
      try {
        await auth.currentUser.updatePassword(newPassword);
      } catch (error: any) {
        if (error.code === 'auth/requires-recent-login') {
          throw new Error("For security, please logout and login again before changing your password.");
        }
        throw error;
      }
    } else {
      throw new Error("No user logged in");
    }
  },

  updateUserEmail: async (newEmail: string): Promise<void> => {
    if (auth.currentUser) {
      try {
        await auth.currentUser.updateEmail(newEmail);
        const uid = auth.currentUser.uid;
        await db.collection("users").doc(uid).update({ email: newEmail });
        await db.collection("configs").doc(uid).update({ contactEmail: newEmail });
      } catch (error: any) {
        if (error.code === 'auth/requires-recent-login') {
          throw new Error("For security, please logout and login again before changing your email.");
        } else if (error.code === 'auth/email-already-in-use') {
          throw new Error("This email is already in use by another account.");
        }
        throw error;
      }
    } else {
      throw new Error("No user logged in");
    }
  },

  getAllUsers: async (): Promise<User[]> => {
    const userSnapshot = await db.collection("users").get();
    const usersList: User[] = [];
    for (const uDoc of userSnapshot.docs) {
      const userData = uDoc.data();
      const configDoc = await db.collection("configs").doc(uDoc.id).get();
      usersList.push({
        id: uDoc.id,
        ...userData,
        config: configDoc.exists ? configDoc.data() as UserConfig : getDefaultConfig(userData.email)
      } as User);
    }
    return usersList;
  },

  // Used by Admin to create users
  createUser: async (email: string, plan: PlanType, expirationDate?: string): Promise<{ user: User, password: string }> => {
    // Only admin can create users
    // This is a simplified check. Security rules should also enforce this.

    try {
      // Generate a secure password: 12 chars, mixed case, numbers, symbols
      const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
      let password = "";
      for (let i = 0; i < 12; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }

      // Use unique name to prevent collisions for secondary app
      const appName = `Secondary-${Date.now()}-${Math.random()}`;
      const secondaryApp = firebase.initializeApp(firebaseConfig, appName);
      const secondaryAuth = secondaryApp.auth();

      const userCredential = await secondaryAuth.createUserWithEmailAndPassword(email, password);
      const uid = userCredential.user!.uid;

      // Calculate expiration date
      let subscriptionEnd = '';
      if (expirationDate) {
        subscriptionEnd = expirationDate;
      } else {
        const today = new Date();
        today.setDate(today.getDate() + 30);
        subscriptionEnd = today.toISOString().split('T')[0];
      }

      const newUser = {
        email: email,
        role: plan === 'agency' ? 'agency' : 'user',
        plan: plan,
        subscriptionStatus: 'active', // Should be active initially if date is future
        subscriptionEnd: subscriptionEnd
      };

      await db.collection("users").doc(uid).set(newUser);

      const defaultConfig = getDefaultConfig(email);
      await db.collection("configs").doc(uid).set(defaultConfig);

      await secondaryApp.delete();

      // alert(`User created successfully!\nEmail: ${email}\nTemporary Password: ${password}`); // Removed alert as per instruction to return
      return { user: { id: uid, ...newUser, config: defaultConfig } as User, password };
    } catch (e: any) {
      try {
        // Attempt to delete secondary app even if user creation failed
        const secondaryApp = firebase.apps.find(app => app.name.startsWith('Secondary-'));
        if (secondaryApp) await secondaryApp.delete();
      } catch (deleteError) {
        console.error("Error deleting secondary app:", deleteError);
      }
      console.error("Error creating user:", e);
      throw new Error(e.message || "Failed to create user");
    }
  },

  // --- AGENCY FEATURES ---

  getAgencyClients: async (agencyId: string, assignedAgentId?: string, includeMembers: boolean = false): Promise<User[]> => {
    try {
      let docs = [];

      if (assignedAgentId) {
        // 1. Fetch Assigned Clients
        const clientsQuery = db.collection("users").where("assignedAgentIds", "array-contains", assignedAgentId);

        // 2. If members requested, fetch them separately (they are not "assigned" to agents)
        let membersQuery = null;
        if (includeMembers) {
          // Verify if we can use 'in' operator or simple equality. 'in' is safer for owner+members
          membersQuery = db.collection("users")
            .where("agencyId", "==", agencyId)
            .where("role", "in", ["agency_member", "agency"]);
        }

        const [clientsSnap, membersSnap] = await Promise.all([
          clientsQuery.get(),
          membersQuery ? membersQuery.get() : Promise.resolve(null)
        ]);

        docs = clientsSnap.docs;
        if (membersSnap) {
          // Merge and Deduplicate (just in case)
          const existingIds = new Set(docs.map(d => d.id));
          membersSnap.docs.forEach(d => {
            if (!existingIds.has(d.id)) {
              docs.push(d);
            }
          });
        }
      } else {
        // Fetch All (Clients + Members mixed)
        const query = db.collection("users").where("agencyId", "==", agencyId);
        const snapshot = await query.get();
        docs = snapshot.docs;
      }

      console.log(`[getAgencyClients] Fetching for agency ${agencyId}, assignedTo: ${assignedAgentId}. Found: ${docs.length}`);

      const clients: User[] = [];
      for (const doc of docs) {
        const data = doc.data();
        // Filter out Agency Members (Employees) unless specifically requested
        if (!includeMembers && (data.role === 'agency_member' || data.role === 'agency')) continue;

        const configSnap = await db.collection("configs").doc(doc.id).get();
        clients.push({
          id: doc.id,
          ...data,
          // Safe handling for createdAt if it's a Firestore Timestamp
          createdAt: (data.createdAt && typeof data.createdAt.toDate === 'function')
            ? data.createdAt.toDate().toISOString()
            : (data.createdAt || new Date().toISOString()),
          config: configSnap.exists ? configSnap.data() : getDefaultConfig(data.email)
        } as User);
      }
      return clients;
    } catch (error: any) {
      console.error("Error fetching agency clients:", error);
      // alert("Error fetching clients: " + error.message); 
      // User reported that they see 0 clients, so maybe it's NOT an error but an empty list. 
      // But if it IS an error, we need to know.
      alert(`Debug Error: Failed to fetch clients. Reason: ${error.message || error.code}`);
      return [];
    }
  },

  createAgencyClient: async (agencyId: string, email: string): Promise<{ user: User, password: string }> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/agency/clients`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to create client');
    }

    const { user, password } = await response.json();
    return { user: user as User, password };
  },

  createAgencyMember: async (agencyId: string, data: { email: string, name: string, phone: string, password?: string, teamRole?: string, departmentId?: string }): Promise<{ user: User, password: string }> => {
    // Client-side creation using Secondary App to avoid logging out current user
    const appName = `AgencyMember-${Date.now()}-${Math.random()}`;
    const secondaryApp = firebase.initializeApp(firebaseConfig, appName);
    const secondaryAuth = secondaryApp.auth();

    try {
      // Use provided password or generate one
      const password = data.password || Math.random().toString(36).slice(-8) + "Aa1!";

      const userCredential = await secondaryAuth.createUserWithEmailAndPassword(data.email, password);
      const uid = userCredential.user!.uid;

      const newMember: any = { // Using any to bypass strict type check for now if needed, or shape it as User
        email: data.email,
        role: 'agency_member',
        agencyId: agencyId,
        plan: 'free', // Member doesn't need a plan
        subscriptionStatus: 'active',
        createdAt: new Date().toISOString(),
        memberProfile: {
          name: data.name,
          phone: data.phone,
          teamRole: data.teamRole || 'standard',
          departmentId: data.departmentId || null
        },
        assignedAgentIds: [] // Can be assigned clients later
      };

      // Write to Firestore using the PRIMARY app (current logged in agency admin)
      // This requires the Firestore Rules update we just made!
      await db.collection("users").doc(uid).set(newMember);

      // Init Config
      const defaultConfig = getDefaultConfig(data.email);
      await db.collection("configs").doc(uid).set(defaultConfig);

      await secondaryApp.delete();

      return { user: { id: uid, ...newMember, config: defaultConfig } as User, password };

    } catch (e: any) {
      try { await secondaryApp.delete(); } catch { }
      console.error("Create Member Error:", e);
      throw new Error(e.message || "Failed to create member");
    }
  },

  updateAgencyMember: async (memberId: string, data: { name: string, phone: string, teamRole: string, departmentId?: string }): Promise<void> => {

    const updateData: any = {
      "memberProfile.name": data.name,
      "memberProfile.phone": data.phone,
      "memberProfile.teamRole": data.teamRole,
    };

    if (data.departmentId !== undefined) {
      updateData["memberProfile.departmentId"] = data.departmentId;
    }

    await db.collection("users").doc(memberId).update(updateData);
  },

  deleteAgencyMember: async (memberId: string, agencyId: string): Promise<void> => {
    // Use Backend to delete Auth User + Firestore Data
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    // Get current user token for auth
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not authenticated");
    const token = await currentUser.getIdToken();

    const response = await fetch(`${API_BASE_URL}/api/agency/members/${memberId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to delete member');
    }
  },

  forceDeleteUserByEmail: async (email: string): Promise<void> => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not authenticated");
    const token = await currentUser.getIdToken();

    const response = await fetch(`${API_BASE_URL}/api/admin/force-delete-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to force delete user');
    }
  },

  deleteAgencyClient: async (clientId: string, agencyId: string): Promise<void> => {
    // 1. Delete all documents associated with the client (Files/Firestore references)
    // We keep this client-side for now as it handles Storage deletion which is complex to move without signed URLs or Admin SDK storage buckets fully setup
    try {
      const clientDocs = await api.getDocuments(agencyId, clientId);
      for (const doc of clientDocs) {
        await api.deleteDocument(doc.id, doc.storagePath);
      }
    } catch (e) {
      console.error("Error deleting client documents:", e);
      // Continue to delete the user even if documents fail
    }

    // 2. Call Backend to Delete User, Config, and Auth Record
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not authenticated");
    const token = await currentUser.getIdToken();

    const response = await fetch(`${API_BASE_URL}/api/agency/clients/${clientId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to delete client');
    }
  },



  // --- DOCUMENTS MODULE (AGENCY & CLIENT) ---

  // 1. Document Types (Agency Only)
  createDocumentType: async (type: Omit<DocumentType, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    const docRef = await db.collection("documentTypes").add({
      ...type,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return docRef.id;
  },

  getDocumentTypes: async (agencyId: string, isClientView: boolean = false): Promise<DocumentType[]> => {
    let query = db.collection("documentTypes").where("agencyId", "==", agencyId);

    // Convert to regular Firestore query before applying further filters
    // Note: In client-side SDK we chain directly.

    if (isClientView) {
      query = query.where("isVisibleToClient", "==", true);
    }

    const snapshot = await query.get();
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DocumentType));
  },

  // Fetch Public Profile (Company Name) via Backend (Bypasses Firestore Rules)
  getPublicProfile: async (userId: string): Promise<{ companyName: string }> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    // Fallback URL if env var is missing during dev
    const baseUrl = API_BASE_URL.startsWith('http') ? API_BASE_URL : 'http://localhost:3001';

    const response = await fetch(`${baseUrl}/api/public/profile/${userId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch public profile');
    }
    return response.json();
  },

  updateDocumentType: async (id: string, updates: Partial<DocumentType>): Promise<void> => {
    await db.collection("documentTypes").doc(id).update({
      ...updates,
      updatedAt: new Date().toISOString()
    });
  },

  deleteDocumentType: async (id: string): Promise<void> => {
    // Hard delete for now, or soft delete if preferred
    await db.collection("documentTypes").doc(id).delete();
  },

  // --- COST CENTERS ---

  createCostCenter: async (agencyId: string, data: { title: string, code: string }): Promise<string> => {
    const docRef = await db.collection("costCenters").add({
      agencyId,
      title: data.title,
      code: data.code,
      createdAt: new Date().toISOString()
    });
    return docRef.id;
  },

  // --- DEPARTMENTS ---
  getDepartments: async (agencyId: string): Promise<Department[]> => {
    const snapshot = await db.collection("departments")
      .where("agencyId", "==", agencyId)
      .get();
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Department));
  },

  createDepartment: async (agencyId: string, data: { name: string }): Promise<void> => {
    await db.collection("departments").add({
      agencyId,
      name: data.name,
      createdAt: new Date().toISOString()
    });
  },

  updateDepartment: async (id: string, updates: Partial<Department>): Promise<void> => {
    await db.collection("departments").doc(id).update(updates);
  },

  deleteDepartment: async (id: string): Promise<void> => {
    await db.collection("departments").doc(id).delete();
  },

  // --- ASSET CATEGORIES ---
  getAssetCategories: async (agencyId: string): Promise<AssetCategory[]> => {
    try {
      const snapshot = await db.collection("asset_categories")
        .where("agencyId", "==", agencyId)
        .orderBy("createdAt", "desc")
        .get();

      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AssetCategory));
    } catch (error) {
      console.error("Error fetching asset categories:", error);
      return [];
    }
  },

  createAssetCategory: async (agencyId: string, data: Omit<AssetCategory, 'id' | 'agencyId' | 'createdAt'>): Promise<void> => {
    await db.collection("asset_categories").add({
      agencyId,
      ...data,
      createdAt: new Date().toISOString()
    });
  },

  updateAssetCategory: async (id: string, updates: Partial<AssetCategory>): Promise<void> => {
    await db.collection("asset_categories").doc(id).update(updates);
  },

  deleteAssetCategory: async (id: string): Promise<void> => {
    await db.collection("asset_categories").doc(id).delete();
  },

  // --- MANUFACTURERS ---
  getManufacturers: async (agencyId: string): Promise<Manufacturer[]> => {
    try {
      const snapshot = await db.collection("manufacturers")
        .where("agencyId", "==", agencyId)
        .orderBy("createdAt", "desc")
        .get();
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Manufacturer));
    } catch (error) {
      console.error("Error fetching manufacturers:", error);
      return [];
    }
  },

  createManufacturer: async (agencyId: string, name: string): Promise<string> => {
    const docRef = await db.collection("manufacturers").add({
      agencyId,
      name,
      createdAt: new Date().toISOString()
    });
    return docRef.id;
  },

  // --- ASSETS ---
  getAssets: async (
    agencyId: string,
    lastVisible: any = null,
    limitCount: number = 3,
    filters?: {
      search?: string;
      status?: string;
      branchId?: string;
      branchIds?: string[];
      costCenterId?: string;
      categoryId?: string;
      categoryIds?: string[];
      supplierId?: string;
      assetManagerId?: string;
      assetUserId?: string;
    }
  ): Promise<{ data: Asset[], lastVisible: any, hasMore: boolean, totalCount: number }> => {
    try {
      // 0. Direct Lookup for Exact Matches (Code, Barcode, Serial)
      // This fixes the issue where a newly imported asset (or one far down the list) isn't found
      // because client-side search only sees the first loaded page.
      let directMatches: Asset[] = [];
      if (filters?.search) {
        const searchTerm = filters.search.trim();
        // We run these in parallel. 
        // Note: Firestore "in" queries are limited, so separate queries are safer for different fields.
        // Also simpler than compound indexes for every combination.
        try {
          const searchQueries = [
            db.collection("assets").where("agencyId", "==", agencyId).where("code", "==", searchTerm).limit(5).get(),
            db.collection("assets").where("agencyId", "==", agencyId).where("barcode", "==", searchTerm).limit(5).get(),
            db.collection("assets").where("agencyId", "==", agencyId).where("serialNumber", "==", searchTerm).limit(5).get(),
            // Description Prefix Search (Case-sensitive)
            db.collection("assets").where("agencyId", "==", agencyId)
              .where("description", ">=", searchTerm)
              .where("description", "<=", searchTerm + '\\uf8ff')
              .limit(5).get()
          ];

          const results = await Promise.all(searchQueries);
          const seenIds = new Set<string>();

          results.forEach(snap => {
            snap.docs.forEach(doc => {
              if (!seenIds.has(doc.id)) {
                seenIds.add(doc.id);
                const asset = { id: doc.id, ...doc.data() } as Asset;
                // Apply in-memory filtering for strict correctness (e.g. if user searching within a specific Branch)
                let passes = true;
                if (filters.branchId && asset.branchId !== filters.branchId) passes = false;
                if (filters.status && asset.status !== filters.status) passes = false;
                // Add more filters if needed, but usually Search overrides strict lists in user mental model.
                // Let's be strict to avoid confusion (e.g. searching archived asset when in Active view).
                if (passes) {
                  directMatches.push(asset);
                }
              }
            });
          });
          if (directMatches.length > 0) {
            console.log(`[API] Direct search found ${directMatches.length} matches for '${searchTerm}'`);
          }
        } catch (err) {
          console.error("[API] Direct search failed", err);
          // Continue to main query fallback
        }
      }

      let assetQuery = db.collection("assets")
        .where("agencyId", "==", agencyId)
        .orderBy("createdAt", "desc");

      if (filters) {
        // ... existing filters ...
        if (filters.status) assetQuery = assetQuery.where("status", "==", filters.status);
        if (filters.branchIds && filters.branchIds.length > 0) {
          assetQuery = assetQuery.where("branchId", "in", filters.branchIds.slice(0, 10));
        } else if (filters.branchId) {
          assetQuery = assetQuery.where("branchId", "==", filters.branchId);
        }
        if (filters.costCenterId) assetQuery = assetQuery.where("costCenterId", "==", filters.costCenterId);
        if (filters.categoryIds && filters.categoryIds.length > 0) {
          assetQuery = assetQuery.where("categoryId", "in", filters.categoryIds);
        } else if (filters.categoryId) {
          assetQuery = assetQuery.where("categoryId", "==", filters.categoryId);
        }
        if (filters.supplierId) assetQuery = assetQuery.where("supplierId", "==", filters.supplierId);
        if (filters.assetManagerId) assetQuery = assetQuery.where("assetManagerId", "==", filters.assetManagerId);
        if (filters.assetUserId) assetQuery = assetQuery.where("assetUserId", "==", filters.assetUserId);

        if (filters.search) {
          // Basic prefix search on 'code' OR use a keywords array if we implement it.
          // Ideally we'd filter by 'keywords' array-contains, but checking multiple fields is hard.
          // For now, let's filter purely client side for search if the result set is small, 
          // OR implement a specific "search" index. 
          // Given the requirements, let's use a "keywords" array check on the server if possible.
          // But first we'd need to migrate data. 
          // LIMITATION: Mixing Equality filters + Array-Contains + OrderBy usually requires Composite Indexes.
          // Fallback: We will filter by EXACT fields, and if SEARCH is present, we might have to rely on
          // just the primary filters + client side search if < 100 items, or specific keyword search.

          // Let's assume we add '_keywords' array to docs.
          const term = filters.search.toLowerCase().trim();
          // assetQuery = assetQuery.where("_keywords", "array-contains", term); 
          // Note: Can't chain array-contains with other inequalities easily without index.
        }
      }

      // Count Query using Modular SDK
      const dbModular = getFirestore(app);
      const assetsRef = collection(dbModular, 'assets');
      let qCount = query(assetsRef, where('agencyId', '==', agencyId));

      if (filters) {
        if (filters.status) qCount = query(qCount, where('status', '==', filters.status));
        if (filters.branchId) qCount = query(qCount, where('branchId', '==', filters.branchId));
        if (filters.costCenterId) qCount = query(qCount, where('costCenterId', '==', filters.costCenterId));
        if (filters.categoryIds && filters.categoryIds.length > 0) {
          qCount = query(qCount, where('categoryId', 'in', filters.categoryIds));
        } else if (filters.categoryId) {
          qCount = query(qCount, where('categoryId', '==', filters.categoryId));
        }
        if (filters.supplierId) qCount = query(qCount, where('supplierId', '==', filters.supplierId));
        if (filters.assetManagerId) qCount = query(qCount, where('assetManagerId', '==', filters.assetManagerId));
        if (filters.assetUserId) qCount = query(qCount, where('assetUserId', '==', filters.assetUserId));
      }

      const countSnapshot = await getCountFromServer(qCount);
      const totalCount = countSnapshot.data().count;

      if (lastVisible) {
        assetQuery = assetQuery.startAfter(lastVisible);
      }

      assetQuery = assetQuery.limit(limitCount + 1); // Fetch one extra to check for 'hasMore'

      const snapshot = await assetQuery.get();
      let docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Asset));

      // Merge Direct Matches at the TOP
      if (directMatches.length > 0) {
        const mainIds = new Set(docs.map(d => d.id));
        const uniqueDirect = directMatches.filter(d => !mainIds.has(d.id));
        docs = [...uniqueDirect, ...docs];
      }

      // Client-side text filtering for this page (imperfect but partial solution if no improved search backend)
      // IF we want true search + pagination we need Algolia.
      // Or we accept that "Search" resets pagination and searches differently.
      if (filters?.search) {
        const term = filters.search.toLowerCase();
        docs = docs.filter(d =>
          d.code.toLowerCase().includes(term) ||
          d.description.toLowerCase().includes(term) ||
          d.serialNumber?.toLowerCase().includes(term) ||
          d.model?.toLowerCase().includes(term) ||
          d.barcode?.toLowerCase().includes(term)
        );
      }

      const hasMore = docs.length > limitCount;
      if (hasMore && !filters?.search) { // If searching client side, the 'hasMore' logic is flawed unless we fetched ALL.
        // Correction: If user searches, pagination breaks unless we use a specialized search query.
        // For now, let's implement the Server Side pagination for NON-SEARCH, 
        // and for SEARCH, effectively "Search within results" or (better) fetch all matching keywords.
        docs.pop();
      }

      return {
        data: docs,
        lastVisible: docs.length > 0 ? (snapshot.docs.find(d => d.id === docs[docs.length - 1].id) || null) : null,
        hasMore,
        totalCount
      };
    } catch (error) {
      console.error("Error fetching assets:", error);
      return { data: [], lastVisible: null, hasMore: false, totalCount: 0 };
    }
  },




  getAssetBySerial: async (agencyId: string, serialNumber: string): Promise<Asset | null> => {
    try {
      const snapshot = await db.collection("assets")
        .where("agencyId", "==", agencyId)
        .where("serialNumber", "==", serialNumber)
        .limit(1)
        .get();

      if (snapshot.empty) return null;
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() } as Asset;
    } catch (e) {
      console.error("Error finding asset by serial:", e);
      return null;
    }
  },

  // --- ASSET HISTORY ---
  getAssetHistory: async (assetId: string): Promise<AssetHistoryEvent[]> => {
    try {
      const snapshot = await db.collection("assets").doc(assetId).collection("history")
        .orderBy("createdAt", "desc")
        .get();
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AssetHistoryEvent));
    } catch (e) {
      console.error("Error fetching asset history:", e);
      return [];
    }
  },

  createAsset: async (agencyId: string, data: Omit<Asset, 'id' | 'agencyId' | 'createdAt' | 'updatedAt'>): Promise<void> => {
    // 1. Generate new ID reference primarily to determine groupId for self-master
    const docRef = db.collection("assets").doc();
    const newId = docRef.id;

    let groupId = newId; // Default: Self is master group
    let masterSerial = data.masterSerial || null;

    // 2. If Master Serial provided, try to link
    if (masterSerial) {
      masterSerial = masterSerial.trim(); // Ensure trim
      // Find master
      try {
        const snapshot = await db.collection("assets")
          .where("agencyId", "==", agencyId)
          .where("serialNumber", "==", masterSerial)
          .limit(1)
          .get();

        if (!snapshot.empty) {
          const masterAsset = snapshot.docs[0].data() as Asset;
          // Inherit Group ID from Master (or use Master's ID if it has no group yet? Schema says all should have group)
          // Fallback: If master has no groupId (legacy), use master's ID.
          groupId = masterAsset.groupId || snapshot.docs[0].id;
        } else {
          // Requirement: Strict Validation.
          throw new Error(`Master Asset with Serial Number "${masterSerial}" not found.`);
        }
      } catch (e: any) {
        console.error("Error linking to master asset", e);
        throw new Error(e.message || "Failed to link Master Asset.");
      }
    }

    const createdDate = new Date().toISOString();

    await docRef.set({
      id: newId,
      agencyId,
      ...data,
      groupId,
      masterSerial,
      createdAt: createdDate,
      updatedAt: createdDate
    });

    // Create Initial History Event
    const currentUser = auth.currentUser;
    // We fetch user details for history logging. Should ideally be passed or cached.
    // For now, we take safer approach of "Unknown" if not readily available in this scope,
    // but usually the caller context might have it.
    // We will rely on client-side or assume context availability.
    // OPTIMIZATION: We can fetch user profile once or pass it.
    // Let's rely on auth object availability.
    let userName = 'System Admin';
    if (currentUser) {
      // We'd ideally need the user profile name.
      // For simplicity/speed in this method, we might just store ID and let UI resolve,
      // OR fetch it. Fetching adds latency.
      // Let's try to get from cache/caller?
      // Since we are in api.ts, we can try to get it if critical.
      // Or store just ID and email.
      userName = currentUser.email || 'Admin';
    }

    const historyRef = docRef.collection("history").doc();
    await historyRef.set({
      id: historyRef.id,
      assetId: newId,
      userId: currentUser?.uid || 'system',
      userName: userName,
      actionType: 'created',
      description: 'Asset Created',
      createdAt: createdDate
    });
  },

  updateAsset: async (id: string, updates: Partial<Asset>, userId?: string, userName?: string): Promise<void> => {
    // Fetch current state for diffing
    const currentDoc = await db.collection("assets").doc(id).get();
    if (!currentDoc.exists) throw new Error("Asset not found");
    const currentData = currentDoc.data() as Asset;

    // Changes Tracking
    // Generic Changes Tracking
    const genericChanges: { field: string, oldValue: any, newValue: any }[] = [];
    let genericActionType: AssetHistoryEvent['actionType'] = 'updated';

    // Independent Events (Manager, User)
    const independentEvents: Partial<AssetHistoryEvent>[] = [];

    // Simple diff logic for critical fields
    // Simple diff logic for critical fields
    if (updates.status && updates.status !== currentData.status) {
      genericChanges.push({ field: 'status', oldValue: currentData.status || null, newValue: updates.status || null });
      genericActionType = 'status_change';
    }
    if (updates.branchId && updates.branchId !== currentData.branchId) {
      genericChanges.push({ field: 'branchId', oldValue: currentData.branchId || null, newValue: updates.branchId || null });
      genericActionType = 'location_change';
    }
    if (updates.costCenterId && updates.costCenterId !== currentData.costCenterId) {
      genericChanges.push({ field: 'costCenterId', oldValue: currentData.costCenterId || null, newValue: updates.costCenterId || null });
      genericActionType = 'location_change';
    }

    // Split Assignments
    if (updates.assetManagerId && updates.assetManagerId !== currentData.assetManagerId) {
      independentEvents.push({
        actionType: 'manager_change',
        description: 'Manager Changed',
        changes: [{ field: 'assetManagerId', oldValue: currentData.assetManagerId || null, newValue: updates.assetManagerId || null }]
      });
    }
    if (updates.assetUserId && updates.assetUserId !== currentData.assetUserId) {
      independentEvents.push({
        actionType: 'user_change',
        description: 'User Changed',
        changes: [{ field: 'assetUserId', oldValue: currentData.assetUserId || null, newValue: updates.assetUserId || null }]
      });
    }

    if (updates.exactLocation && updates.exactLocation !== currentData.exactLocation) {
      genericChanges.push({ field: 'exactLocation', oldValue: currentData.exactLocation || null, newValue: updates.exactLocation || null });
      genericActionType = 'location_change';
    }

    // If masterSerial is being updated, we need to re-calculate groupId
    let newGroupId = updates.groupId;

    if (updates.masterSerial !== undefined) {
      if (updates.masterSerial === null || updates.masterSerial === '') {
        // Detach: become own master
        // Safer to reset to self group id
        newGroupId = id;
      } else {
        // Attach to new master
        try {
          // Ensure trim
          const serialToFind = updates.masterSerial.trim();

          let snapshot = await db.collection("assets")
            .where("agencyId", "==", currentData.agencyId)
            .where("serialNumber", "==", serialToFind)
            .limit(1)
            .get();

          // If not found by serial, try finding by CODE (since new import strategy uses Code as master reference)
          if (snapshot.empty) {
            snapshot = await db.collection("assets")
              .where("agencyId", "==", currentData.agencyId)
              .where("code", "==", serialToFind)
              .limit(1)
              .get();
          }

          if (!snapshot.empty) {
            const master = snapshot.docs[0].data() as Asset;
            // Prevent circular reference: Check if master IS self
            if (master.id === id) {
              throw new Error("Cannot link asset to itself as Master.");
            }
            // Inherit group
            newGroupId = master.groupId || snapshot.docs[0].id;
          } else {
            throw new Error(`Master Asset with Serial Number or Code "${serialToFind}" not found.`);
          }
        } catch (e: any) {
          console.error("Error resolve master during update", e);
          throw new Error(e.message || "Failed to link Master Asset.");
        }
      }
    }

    await db.collection("assets").doc(id).update({
      ...updates,
      ...(newGroupId && { groupId: newGroupId }),
      updatedAt: new Date().toISOString()
    });

    // Log History if changes detected
    // Log Independent Events
    for (const evt of independentEvents) {
      const historyRef = db.collection("assets").doc(id).collection("history").doc();
      await historyRef.set({
        id: historyRef.id,
        assetId: id,
        userId: userId || auth.currentUser?.uid || 'system',
        userName: userName || 'Admin',
        createdAt: new Date().toISOString(),
        ...evt
      });
    }

    // Log Generic Changes
    if (genericChanges.length > 0) {
      const historyRef = db.collection("assets").doc(id).collection("history").doc();
      await historyRef.set({
        id: historyRef.id,
        assetId: id,
        userId: userId || auth.currentUser?.uid || 'system',
        userName: userName || 'Admin',
        actionType: genericActionType,
        description: genericActionType === 'status_change' ? 'Status Change' : 'Asset Updated',
        changes: genericChanges,
        createdAt: new Date().toISOString()
      });
    }
  },

  deleteAsset: async (id: string): Promise<void> => {
    // Check for children before deleting
    try {
      const docSnap = await db.collection("assets").doc(id).get();
      if (!docSnap.exists) return; // Already deleted
      const assetData = docSnap.data() as Asset;
      const agencyId = assetData.agencyId;

      // Find potential children (assets with this groupId, excluding self)
      // Note: Master usually has groupId == id. Children have groupId == masterId.
      const childrenSnapshot = await db.collection("assets")
        .where("agencyId", "==", agencyId) // Security Rule Requirement
        .where("groupId", "==", id)
        .limit(10) // Check a few
        .get();

      const hasChildren = childrenSnapshot.docs.some(doc => doc.id !== id);

      if (hasChildren) {
        throw new Error("Cannot delete Master Asset that has linked Child Assets. Please detach or delete children first.");
      }
    } catch (error: any) {
      if (error.message.includes("Cannot delete")) throw error;
      console.error("Error checking children before delete", error);
      throw new Error("Could not verify asset dependencies. Start delete failed.");
    }

    // Delete History Subcollection
    try {
      const historySnapshot = await db.collection("assets").doc(id).collection("history").get();
      if (!historySnapshot.empty) {
        const batch = db.batch();
        historySnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
        await batch.commit();
        console.log(`Deleted ${historySnapshot.size} history events for asset ${id}`);
      }
    } catch (e) {
      console.error("Failed to delete asset history:", e);
      // Proceed to delete asset even if history delete fails (or throw? defaulting to proceed)
    }

    await db.collection("assets").doc(id).delete();
  },

  deleteAllAssets: async (agencyId: string): Promise<void> => {
    // 1. Get all assets
    const snapshot = await db.collection("assets").where("agencyId", "==", agencyId).get();
    if (snapshot.empty) return;

    // 2. Batch Delete
    // Firestore batch limit is 500. We need to chunk.
    const batchSize = 400; // Safe margin
    const chunks = [];
    for (let i = 0; i < snapshot.size; i += batchSize) {
      chunks.push(snapshot.docs.slice(i, i + batchSize));
    }

    console.log(`[API] Deleting ${snapshot.size} assets in ${chunks.length} batches.`);

    for (const chunk of chunks) {
      const batch = db.batch();
      // For each asset, we ideally should delete its history subcollection too.
      // But recursively deleting subcollections from CLIENT SDK is hard/slow.
      // If we just delete the parent doc, the subcollection remains orphaned but inaccessible via normal queries.
      // Given the requirement "delete indexes", deleting the main doc removes it from queries.
      // To be cleaner, we can try to delete history for each, but that would require N reads.
      // Let's stick to deleting the Asset Documents for now as it's the primary requirement.
      chunk.forEach(doc => {
        batch.delete(doc.ref);
      });
      await batch.commit();
    }
    console.log("[API] All assets deleted.");
  },

  // --- COST CENTERS ---
  getCostCenters: async (agencyId: string): Promise<CostCenter[]> => {
    try {
      const snapshot = await db.collection("costCenters")
        .where("agencyId", "==", agencyId)
        .orderBy("createdAt", "desc")
        .get();

      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CostCenter));
    } catch (error) {
      console.error("Error fetching cost centers:", error);
      return [];
    }
  },

  updateCostCenter: async (id: string, updates: { title?: string, code?: string }): Promise<void> => {
    await db.collection("costCenters").doc(id).update(updates);
  },

  deleteCostCenter: async (id: string): Promise<void> => {
    await db.collection("costCenters").doc(id).delete();
  },

  // --- SUPPLIERS ---

  getSuppliers: async (agencyId: string): Promise<Supplier[]> => {
    try {
      const snapshot = await db.collection("suppliers")
        .where("agencyId", "==", agencyId)
        .orderBy("createdAt", "desc")
        .get();

      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Supplier));
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      return [];
    }
  },

  createSupplier: async (agencyId: string, data: Omit<Supplier, 'id' | 'agencyId' | 'createdAt'>): Promise<string> => {
    const docRef = await db.collection("suppliers").add({
      agencyId,
      ...data,
      createdAt: new Date().toISOString()
    });
    return docRef.id;
  },

  updateSupplier: async (id: string, updates: Partial<Omit<Supplier, 'id' | 'agencyId' | 'createdAt'>>): Promise<void> => {
    await db.collection("suppliers").doc(id).update(updates);
  },

  deleteSupplier: async (id: string): Promise<void> => {
    await db.collection("suppliers").doc(id).delete();
  },

  // 2. Documents (Instances)
  createDocument: async (doc: Omit<Document, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    const docRef = await db.collection("documents").add({
      ...doc,
      status: 'pending', // Always pending on creation
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return docRef.id;
  },

  getDocuments: async (agencyId: string, clientId?: string): Promise<Document[]> => {
    let query = db.collection("documents").where("agencyId", "==", agencyId);

    if (clientId) {
      query = query.where("clientId", "==", clientId);
    }

    const snapshot = await query.get();
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Document));
  },

  updateDocumentStatus: async (id: string, status: 'approved' | 'rejected' | 'archived', moveContext?: { agencyName: string, clientName: string }): Promise<void> => {
    const docRef = db.collection("documents").doc(id);

    // 1. If approving and context provided, try to move file
    if (status === 'approved' && moveContext) {
      console.log(`[Approve] Storage File Move Skipped (Storage Disabled)`);
    } else {
      if (status === 'approved') console.warn(`[Approve] Missing moveContext.`, moveContext);
    }

    // Default Update
    await docRef.update({
      status,
      updatedAt: new Date().toISOString()
    });
  },

  updateDocument: async (id: string, updates: Partial<Document> & { newStoragePath?: string }): Promise<void> => {
    // Use Backend API to handle potential file move and metadata update securely
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not authenticated");
    const token = await currentUser.getIdToken();

    const response = await fetch(`${API_BASE_URL}/api/agency/documents/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to update document');
    }
  },

  deleteDocument: async (id: string, storagePath?: string): Promise<void> => {
    // 1. Delete Storage File - SKIPPED
    if (storagePath) {
      // Storage disabled
    }

    // 2. Delete Notes Subcollection (Manually, as Firestore doesn't cascade)
    try {
      const notesSnapshot = await db.collection("documents").doc(id).collection("notes").get();
      if (!notesSnapshot.empty) {
        const batch = db.batch();
        notesSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
        await batch.commit();
        console.log(`Deleted ${notesSnapshot.size} notes for document ${id}`);
      }
    } catch (e) {
      console.error("Failed to delete document notes:", e);
      // We proceed to delete the main document, though orphans might remain if this fails.
      // Ideally we shouldn't fail main delete for this.
    }

    // 3. Delete Document
    await db.collection("documents").doc(id).delete();
  },

  // --- DOCUMENT NOTES ---

  getDocumentNotes: async (documentId: string): Promise<DocumentNote[]> => {
    try {
      const snapshot = await db.collection("documents").doc(documentId).collection("notes").orderBy("createdAt", "asc").get();
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DocumentNote));
    } catch (error) {
      console.error("Error fetching document notes:", error);
      return [];
    }
  },

  addDocumentNote: async (documentId: string, note: Omit<DocumentNote, "id">): Promise<DocumentNote> => {
    try {
      const docRef = await db.collection("documents").doc(documentId).collection("notes").add(note);

      try {
        const updateData: any = {
          "metadata.notes": true
        };

        if (note.isAgency) {
          updateData["metadata.hasUnreadNotesForClient"] = true;
        } else {
          updateData["metadata.hasUnreadNotesForAgency"] = true;
        }

        await db.collection("documents").doc(documentId).update(updateData);
      } catch (updateError) {
        console.warn("Could not update document metadata for notes indicator:", updateError);
        // We proceed because the note itself was saved successfully
      }

      return { id: docRef.id, ...note };
    } catch (error) {
      console.error("Error adding document note:", error);
      throw error;
    }
  },

  markDocumentNotesAsRead: async (documentId: string, isAgency: boolean): Promise<void> => {
    try {
      const fieldToUpdate = isAgency ? "metadata.hasUnreadNotesForAgency" : "metadata.hasUnreadNotesForClient";
      await db.collection("documents").doc(documentId).update({
        [fieldToUpdate]: false
      });
    } catch (error) {
      console.error("Error marking notes as read:", error);
      // We don't throw here to avoid blocking UI if this fails
    }
  },

  deleteUser: async (userId: string): Promise<void> => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not authenticated");
    const token = await currentUser.getIdToken();

    const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to delete user');
    }
  },

  updateUserConfig: async (userId: string, config: Partial<UserConfig>): Promise<User> => {
    await db.collection("configs").doc(userId).set(config, { merge: true });
    return api.getUser(userId);
  },

  updateUserProfile: async (userId: string, profile: UserProfile): Promise<User> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/users/${userId}/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(profile)
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to update profile');
    }

    return response.json();
  },

  updateClientAssignments: async (clientId: string, assignedAgentIds: string[]): Promise<void> => {
    console.log(`[api] Updating assignments for client ${clientId} to:`, assignedAgentIds);
    await db.collection("users").doc(clientId).set({
      assignedAgentIds: assignedAgentIds
    }, { merge: true });
  },

  validateVat: async (vatNumber: string, countryCode: string = 'GR'): Promise<{ valid: boolean; name?: string; address?: string }> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/check-vat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ vatNumber, countryCode })
    });

    if (!response.ok) {
      throw new Error('VAT Validation failed');
    }

    return response.json();
  },

  updateUserPreferences: async (userId: string, preferences: { timezone?: string; currency?: string; dateFormat?: string; language?: string; }): Promise<void> => {
    await db.collection("users").doc(userId).set(preferences, { merge: true });
  },

  // Upload file to Firebase Storage
  uploadFile: async (path: string, file: File): Promise<string> => {
    throw new Error("Storage is disabled");
    // const ref = storage.ref().child(path);
    // await ref.put(file);
    // return await ref.getDownloadURL();
  },

  getPublicUserConfig: async (userId: string): Promise<{ config: UserConfig | null, status: string, emailJsConfig?: EmailJsConfig }> => {
    try {
      const userSnap = await db.collection("users").doc(userId).get();
      if (!userSnap.exists) {
        return { config: null, status: 'not_found' };
      }
      const userData = userSnap.data();

      // Check Agency Status if applicable
      let status = userData?.subscriptionStatus || 'expired';
      if (userData?.agencyId) {
        const agencySnap = await db.collection("users").doc(userData.agencyId).get();
        if (agencySnap.exists) {
          const agencyData = agencySnap.data();
          if (agencyData?.subscriptionStatus !== 'active') {
            status = 'expired'; // If agency expires, client expires
          }
        }
      }

      if (status !== 'active') {
        return { config: null, status: status };
      }
      const configSnap = await db.collection("configs").doc(userId).get();

      // Fetch public EmailJS settings from global settings or env
      let emailJsConfig: EmailJsConfig | undefined;
      const envServiceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
      const envTemplateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
      const envPublicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

      // Prioritize Env Vars
      if (envServiceId && envTemplateId && envPublicKey) {
        emailJsConfig = {
          serviceId: envServiceId,
          templateId: envTemplateId,
          publicKey: envPublicKey
        };
      } else {
        // Fallback to Database
        try {
          const settingsSnap = await db.collection("settings").doc("global").get();
          if (settingsSnap.exists) {
            const data = settingsSnap.data() as SystemSettings;
            if (data.emailJsServiceId && data.emailJsTemplateId && data.emailJsPublicKey) {
              emailJsConfig = {
                serviceId: data.emailJsServiceId,
                templateId: data.emailJsTemplateId,
                publicKey: data.emailJsPublicKey
              };
            }
          }
        } catch (e) {
          console.warn("Could not fetch global settings for EmailJS");
        }
      }

      if (configSnap.exists) {
        return { config: configSnap.data() as UserConfig, status: 'active', emailJsConfig };
      }
      return { config: null, status: 'config_missing' };
    } catch (e) {
      console.error("Error fetching public config", e);
      return { config: null, status: 'error' };
    }
  },

  // --- NOTIFICATIONS ---
  notifyClient: async (documentId: string, clientId: string, title: string, body: string): Promise<void> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    try {
      const response = await fetch(`${API_BASE_URL}/api/notifications/document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ documentId, clientId, title, body })
      });

      if (!response.ok) {
        const textRaw = await response.text();
        let errMessage = 'Failed to send notification';
        try {
          const errJson = JSON.parse(textRaw);
          errMessage = errJson.error || errMessage;
        } catch (e) {
          // Not JSON
          errMessage = `Server Error (${response.status}): ${textRaw.substring(0, 200)}...`;
        }
        throw new Error(errMessage);
      }
    } catch (e: any) {
      console.error("notifyClient failed:", e);
      throw e;
    }
  },

  // --- STRIPE NATIVE ---

  validateCoupon: async (code: string) => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error("No auth token");
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/validate-coupon`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ code })
    });
    return response.json();
  },

  createCheckoutSession: async (plan: string, interval: string, couponCode?: string) => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error("No auth token");
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        plan,
        interval,
        successUrl: window.location.origin + window.location.pathname + '?session_id={CHECKOUT_SESSION_ID}',
        cancelUrl: window.location.href,
        couponCode
      })
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || response.statusText);
    }

    return response.json();
  },

  // --- ADMIN COUPONS (Stripe) ---

  getAdminCoupons: async () => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const response = await fetch(`${API_BASE_URL}/api/admin/coupons`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch coupons');
    return response.json();
  },

  createAdminCoupon: async (code: string, percentOff: number) => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const response = await fetch(`${API_BASE_URL}/api/admin/coupons`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ code, percentOff })
    });
    if (!response.ok) throw new Error('Failed to create coupon');
    return response.json();
  },

  deleteAdminCoupon: async (id: string) => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const response = await fetch(`${API_BASE_URL}/api/admin/coupons/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to delete coupon');
    return response.json();
  },

  updateSubscription: async (userId: string, plan: PlanType, interval: string = 'monthly'): Promise<void> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const response = await fetch(`${API_BASE_URL}/api/update-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ plan, interval })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update subscription');
    }
  },

  updateUserStatus: async (userId: string, status: 'active' | 'expired' | 'cancelled' | 'inactive'): Promise<void> => {
    await db.collection("users").doc(userId).update({ subscriptionStatus: status });
  },

  extendSubscription: async (userId: string, months: number): Promise<User> => {
    const userRef = db.collection("users").doc(userId);
    const snap = await userRef.get();
    const currentEnd = new Date(snap.data()?.subscriptionEnd || Date.now());
    currentEnd.setMonth(currentEnd.getMonth() + months);
    await userRef.update({
      subscriptionStatus: 'active',
      subscriptionEnd: currentEnd.toISOString().split('T')[0]
    });
    return {} as any;
  },

  getSystemSettings: async (): Promise<SystemSettings> => {
    const docSnap = await db.collection("settings").doc("global").get();
    const envSettings = {
      emailJsServiceId: import.meta.env.VITE_EMAILJS_SERVICE_ID || '',
      emailJsTemplateId: import.meta.env.VITE_EMAILJS_TEMPLATE_ID || '',
      emailJsPublicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY || ''
    };

    if (docSnap.exists) {
      const data = docSnap.data() as SystemSettings;
      return {
        ...data,
        ...envSettings, // Override with env vars if present, or fallback logic if empty? 
        // Actually, let's prioritize Env Vars if they exist.
        emailJsServiceId: envSettings.emailJsServiceId || data.emailJsServiceId || '',
        emailJsTemplateId: envSettings.emailJsTemplateId || data.emailJsTemplateId || '',
        emailJsPublicKey: envSettings.emailJsPublicKey || data.emailJsPublicKey || ''
      };
    }
    return {
      aiApiKey: '',
      ...envSettings
    };
  },

  updateSystemSettings: async (settings: SystemSettings): Promise<void> => {
    await db.collection("settings").doc("global").set(settings, { merge: true });
  },

  getGlobalPricing: async (): Promise<GlobalPricing | null> => {
    try {
      const docSnap = await db.collection("settings").doc("pricing").get();
      if (docSnap.exists) {
        return docSnap.data() as GlobalPricing;
      }
      return {
        [PlanType.LIGHT]: { monthly: 19, yearly: 190 },
        [PlanType.ADVANCED]: { monthly: 49, yearly: 490 },
        [PlanType.AGENCY]: { monthly: 199, yearly: 1990 }
      };
    } catch (error) {
      console.error("Error fetching pricing:", error);
      return null;
    }
  },

  updateGlobalPricing: async (pricing: GlobalPricing): Promise<void> => {
    await db.collection("settings").doc("pricing").set(pricing, { merge: true });
  },

  // --- Appointments ---

  bookAppointment: async (appointment: Omit<Appointment, 'id' | 'createdAt' | 'status'>) => {
    try {
      // 1. Save to Firestore
      const newAppt = {
        ...appointment,
        createdAt: new Date().toISOString(),
        status: 'confirmed'
      };
      await db.collection("appointments").add(newAppt);

      // 2. Log Analytics
      await api.logAnalyticsEvent(appointment.userId, 'booking_request', `${appointment.type} on ${appointment.date} @ ${appointment.time}`);

      return true;
    } catch (error) {
      console.error("Error booking appointment:", error);
      throw error;
    }
  },

  getAppointments: async (userId: string): Promise<Appointment[]> => {
    try {
      const snapshot = await db.collection("appointments")
        .where("userId", "==", userId)
        .get();

      const appointments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Appointment));

      // Sort by Date Descending
      appointments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      return appointments;
    } catch (error) {
      console.error("Error fetching appointments:", error);
      return [];
    }
  },

  updateUser: async (userId: string, data: any): Promise<void> => {
    try {
      // Sync config to 'configs' collection if present
      // Handle both direct 'config' object AND dot notation 'config.field'
      let configUpdate: any = {};

      if (data.config) {
        configUpdate = data.config;
      }

      // Extract keys starting with 'config.'
      Object.keys(data).forEach(key => {
        if (key.startsWith('config.')) {
          // e.g. "config.ai_features.document_ai_enabled" -> "ai_features.document_ai_enabled"
          const configKey = key.substring(7);
          configUpdate[configKey] = data[key];
        }
      });

      if (Object.keys(configUpdate).length > 0) {
        // Check if we have nested fields (like ai_features.enabled) that need to be objects for set(merge)
        // OR if we should just use update().
        // set({ 'a.b': val }, {merge: true}) behaves like update for those fields if they exist?
        // Actually, for set(merge:true), standard object structure is better.
        // But Firestore update() takes dot notation.
        // Let's use update() on configs collection if the doc exists, or set() if not?
        // Safest is set({ ...unflattened }, { merge: true }) or just update(configUpdate) if we keep dot notation.
        // Since we are syncing, let's try update() first, catch error (if doc doesn't exist), then set.

        const configRef = db.collection("configs").doc(userId);
        const doc = await configRef.get();

        if (doc.exists) {
          await configRef.update(configUpdate);
        } else {
          // We need to unflatten if we use set? No, set() with merge:true doesn't support dot notation keys as fields?
          // Actually it does NOT support dot notation keys for nested fields in the same way update does for partials?
          // Wait, update({ "ai_features.enabled": true }) works.
          // set({ "ai_features.enabled": true }) might create a field with dots in the name!
          // We must UNFLATTEN for set(), or use update().
          // Since we know doc exists (or we create default), let's ensure it exists.
          await configRef.set({}, { merge: true }); // Ensure exists
          await configRef.update(configUpdate); // Then update using dot notation
        }
      }

      // Use set with merge: true to handle nested fields via dot notation if passed as flattening keys
      // But firestore.update() handles dot notation natively.
      await db.collection("users").doc(userId).update(data);
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  },

  updateAppointmentStatus: async (appointmentId: string, status: 'pending' | 'confirmed' | 'cancelled'): Promise<void> => {
    try {
      await db.collection("appointments").doc(appointmentId).update({ status });
    } catch (error) {
      console.error("Error updating appointment status:", error);
      throw error;
    }
  },

  // --- Analytics ---

  logAnalyticsEvent: async (userId: string, eventType: 'email_form' | 'phone_click' | 'widget_open' | 'booking_request' | 'call_back' | 'external_link_click', details: string) => {
    // Determine context safely
    const isBrowser = typeof window !== 'undefined';
    let source = 'direct', medium = 'none', campaign = '(not set)', referrer = '', path = '';

    if (isBrowser) {
      const params = new URLSearchParams(window.location.search);
      source = params.get('utm_source') || 'direct';
      medium = params.get('utm_medium') || 'none';
      campaign = params.get('utm_campaign') || '(not set)';
      referrer = document.referrer || '';
      path = window.location.pathname;
    }

    const eventData: Omit<AnalyticsEvent, 'id'> = {
      userId,
      eventType,
      details,
      timestamp: new Date().toISOString(),
      source,
      medium,
      campaign,
      referrer,
      path
    };

    try {
      await db.collection("analytics").add(eventData);
    } catch (e) {
      console.error("Failed to log analytics", e);
    }
  },

  getAnalyticsEvents: async (userId: string, startDate?: Date, endDate?: Date): Promise<AnalyticsEvent[]> => {
    try {
      const snapshot = await db.collection("analytics")
        .where("userId", "==", userId)
        .get();

      let events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as AnalyticsEvent));

      if (startDate && endDate) {
        const s = new Date(startDate); s.setHours(0, 0, 0, 0);
        const e = new Date(endDate); e.setHours(23, 59, 59, 999);

        events = events.filter(ev => {
          const t = new Date(ev.timestamp);
          return t >= s && t <= e;
        });
      }

      events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      return events;
    } catch (e) {
      console.error("Error fetching analytics", e);
      return [];
    }
  },

  // Send Test Analytics Report
  sendTestReport: async (toEmail: string, companyName: string, stats: any, dateRange?: string) => {
    // 1. Check if System Settings are loaded (We need EmailJS credentials OR confirm we use backend)
    // For now, we assume settings are loaded in global state or fetched.
    // Ideally, we fetch them here if missing, but for now we rely on the component passing config or checking settings elsewhere.
    // In this singleton, we can access the 'settings' variable if it acts as a cache, or fetch it.
    // Let's assume 'settings' is populated or we fetch it.
    // Note: The original code accessed 'settings' variable which was likely in closure or module scope.
    // We will ensure we have settings.
    let settings: SystemSettings = { aiApiKey: '', emailJsServiceId: '', emailJsTemplateId: '', emailJsPublicKey: '' };

    /*
      logic to ensure settings are loaded...
    */
    // Fetch System Settings to Check for EmailJS Config
    try {
      const docSnap = await db.collection("settings").doc("global").get();
      if (docSnap.exists) {
        settings = docSnap.data() as SystemSettings;
      }
    } catch (e) {
      console.warn("Could not access system configuration for report.", e);
    }

    if (!settings.emailJsServiceId && !auth.currentUser) {
      // Try fetch if not present
      try {
        const s = await db.collection('settings').doc('global').get(); // Corrected collection name from system_settings to settings
        if (s.exists) {
          const data = s.data() as SystemSettings;
          settings.emailJsServiceId = data.emailJsServiceId;
          settings.emailJsTemplateId = data.emailJsTemplateId;
          settings.emailJsPublicKey = data.emailJsPublicKey;
        }
      } catch (e) {
        console.warn("Could not load system settings for report", e);
      }
    }

    if (!settings.emailJsServiceId && !auth.currentUser) {
      throw new Error("Could not access system configuration.");
    }

    // 2. Try Backend FIRST (Preferred for HTML Template)
    try {
      // GET FIREBASE TOKEN
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("User must be logged in to send reports via backend.");

      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/send-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ toEmail, companyName, stats, dateRange })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send report');
      return data;
    } catch (backendError: any) {
      console.warn("Backend Send Failed, trying EmailJS Fallback:", backendError);

      // 3. Fallback to Client-Side EmailJS
      if (settings.emailJsServiceId && settings.emailJsTemplateId && settings.emailJsPublicKey) {

        // Ensure EmailJS script is loaded
        if (typeof window !== 'undefined' && !(window as any).emailjs) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = "https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js";
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if ((window as any).emailjs) {
          const message = `
ANALYTICS REPORT: ${companyName}
--------------------------------
Widget Opens: ${stats.opens}
Appointments: ${stats.appointments}
Messages:     ${stats.emails}
Phone Clicks: ${stats.clicks}

Login to your dashboard for full details.
               `.trim();

          try {
            await (window as any).emailjs.send(
              settings.emailJsServiceId,
              settings.emailJsTemplateId,
              {
                to_email: toEmail,
                from_email: 'reports@widgetflow.ai',
                message: message,
                company_name: companyName,
                subject: `Analytics Report: ${companyName}`
              },
              settings.emailJsPublicKey
            );
            return { success: true, method: 'emailjs_fallback' };
          } catch (err: any) {
            console.error("EmailJS Client Error:", err);
            throw new Error("Failed to send via Backend AND EmailJS.");
          }
        }
      }
      throw backendError;
    }
  },

  // --- STRIPE SUBSCRIPTIONS ---



  createPortalSession: async (): Promise<{ url: string }> => {
    const token = await auth.currentUser?.getIdToken(true);
    if (!token) throw new Error("User must be logged in");

    // Use environment variable for API URL (production) or default to localhost (development)
    // const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/create-portal-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        returnUrl: window.location.href
      })
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to create portal session');
    return data;
  },

  cancelSubscription: async (): Promise<void> => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error("User must be logged in");

    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to cancel subscription');
    }
  },

  // --- BILLING PROFILE & INVOICES ---

  saveBillingProfile: async (profile: Omit<BillingProfile, 'id' | 'vatValid' | 'taxStatus'>): Promise<BillingProfile> => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error("User must be logged in");
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/billing-profile`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(profile)
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to save billing profile');
    return data;
  },

  getBillingProfiles: async (userId: string): Promise<BillingProfile[]> => {
    // Option A: Use API (if we want to use the one we built)
    // Option B: Direct Firestore
    // Let's use Direct Firestore for read speed in client, assuming rules allow read.
    // However, I implemented GET /api/billing-profile in server.js.
    // Let's use the API to ensure we get any computed fields or logic if added later.
    // Also consistent with save.

    // Actually, for better UX (realtime updates?), Firestore is better. But requirements didn't ask for realtime.
    // Let's use the API I built.

    const token = await auth.currentUser?.getIdToken();
    if (!token) return []; // or throw
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    try {
      const response = await fetch(`${API_BASE_URL}/api/billing-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch');
      return await response.json();
    } catch (e) {
      console.warn("API Fetch failed, trying direct DB fallback needed?");
      // Fallback to direct DB if API fails?
      const snap = await db.collection("users").doc(userId).collection("billing_profiles").get();
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as BillingProfile));
    }
  },

  getPayments: async (userId: string): Promise<Payment[]> => {
    try {
      const snap = await db.collection("users").doc(userId).collection("payments").orderBy("date", "desc").get();
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as Payment));
    } catch (error) {
      console.error("Error fetching payments:", error);
      return [];
    }
  },

  requestInvoice: async (paymentId: string, profileId?: string): Promise<void> => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error("User must be logged in");
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/request-invoice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ paymentId, profileId })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to request invoice');
    }
  },



  // 3. Storage Upload
  // 3. Storage Upload
  uploadDocumentFile: async (file: File, path: string, metadata?: any): Promise<string> => {
    throw new Error("Storage is disabled");
    // const storageRef = storage.ref().child(path);
    // if (metadata) {
    //   await storageRef.put(file, { customMetadata: metadata });
    // } else {
    //   await storageRef.put(file);
    // }
    // return await storageRef.getDownloadURL();
  },

  getAllBillingProfilesAdmin: async (): Promise<any[]> => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error("User must be logged in");
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/admin/billing-profiles`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) throw new Error('Failed to fetch admin data');
    return await response.json();
  },

  sendDocumentNotification: async (data: {
    clientId: string;
    docTitle?: string;
    docType?: string;
    docTypeId?: string;
    agencyName: string;
    documents?: Array<{
      title: string;
      typeName: string;
      typeId?: string;
      date: string;
    }>
  }): Promise<void> => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error("User must be logged in");
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/send-document-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) throw new Error('Failed to send notification');
    if (!response.ok) throw new Error('Failed to send notification');
  },

  getDocumentsByIds: async (docIds: string[]): Promise<Document[]> => {
    if (!docIds || docIds.length === 0) return [];
    // Use Modular SDK 'in' query in batches of 30
    // We will perform parallel requests if > 30

    const dbModular = getFirestore(app);
    const docsRef = collection(dbModular, 'documents');

    const chunks = [];
    for (let i = 0; i < docIds.length; i += 30) {
      chunks.push(docIds.slice(i, i + 30));
    }

    const results = await Promise.all(chunks.map(async (chunk) => {
      const q = query(docsRef, where('__name__', 'in', chunk)); // Query by document ID (__name__)
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Document));
    }));

    return results.flat();
  },

  getPaginatedDocuments: async (
    agencyId: string,
    limitCount: number,
    lastVisible: any = null,
    filters: {
      clientId?: string;
      clientIds?: string[]; // Added support for multiple IDs
      typeId?: string;
      status?: string;
      subStatus?: string;
      uploadedByRole?: string;
      dateFrom?: Date;
      dateTo?: Date;
      hasUnreadNotes?: boolean;
    } = {}
  ): Promise<{ data: Document[]; lastVisible: any; hasMore: boolean; totalCount: number }> => {
    try {
      let q = db.collection('documents')
        .where('agencyId', '==', agencyId)
        .orderBy('createdAt', 'desc');

      if (filters.clientId) {
        q = q.where('clientId', '==', filters.clientId);
      }

      // Handle multiple client IDs (for text search)
      if (filters.clientIds && filters.clientIds.length > 0) {
        // Firestore 'in' limitation: max 30
        const safeIds = filters.clientIds.slice(0, 30);
        q = q.where('clientId', 'in', safeIds);
      }

      if (filters.typeId) {
        q = q.where('typeId', '==', filters.typeId);
      }
      if (filters.status) {
        q = q.where('status', '==', filters.status);
      }
      if (filters.uploadedByRole) {
        q = q.where('uploadedByRole', '==', filters.uploadedByRole);
      }
      if (filters.dateFrom) {
        q = q.where('createdAt', '>=', filters.dateFrom);
      }
      if (filters.dateTo) {
        q = q.where('createdAt', '<=', filters.dateTo);
      }
      if (filters.hasUnreadNotes) {
        q = q.where('metadata.hasUnreadNotesForAgency', '==', true);
      }

      // Count Query using Modular SDK (Compat SDK missing .count())
      const dbModular = getFirestore(app);
      const docsRef = collection(dbModular, 'documents');
      let qCount = query(docsRef, where('agencyId', '==', agencyId));

      if (filters.clientId) qCount = query(qCount, where('clientId', '==', filters.clientId));
      if (filters.clientIds && filters.clientIds.length > 0) qCount = query(qCount, where('clientId', 'in', filters.clientIds.slice(0, 30)));
      if (filters.typeId) qCount = query(qCount, where('typeId', '==', filters.typeId));
      if (filters.status) qCount = query(qCount, where('status', '==', filters.status));
      if (filters.uploadedByRole) qCount = query(qCount, where('uploadedByRole', '==', filters.uploadedByRole));
      if (filters.dateFrom) qCount = query(qCount, where('createdAt', '>=', filters.dateFrom));
      if (filters.dateTo) qCount = query(qCount, where('createdAt', '<=', filters.dateTo));
      if (filters.hasUnreadNotes) qCount = query(qCount, where('metadata.hasUnreadNotesForAgency', '==', true));

      const countSnapshot = await getCountFromServer(qCount);
      const totalCount = countSnapshot.data().count;

      if (lastVisible) {
        q = q.startAfter(lastVisible);
      }

      q = q.limit(limitCount + 1);

      const snapshot = await q.get();
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Document));
      const hasMore = docs.length > limitCount;
      if (hasMore) docs.pop();

      return {
        data: docs,
        lastVisible: docs.length > 0 ? snapshot.docs[docs.length - 1] : null,
        hasMore,
        totalCount
      };
    } catch (error) {
      console.error("Error fetching paginated documents:", error);
      throw error;
    }
  }
};

const getDefaultConfig = (email: string): UserConfig => ({
  companyName: 'My Company',
  websiteUrl: '',
  description: 'Hi, nice to see you here! 👋',
  contactEmail: email,
  phone1: '',
  phone1Label: 'Phone 1',
  phone2: '',
  phone2Label: 'Phone 2',
  phone3: '',
  phone3Label: 'Phone 3',
  mobile: '',
  mobileLabel: 'Mobile',
  contactViber: '',
  contactMessenger: '',
  googlePlaceId: '',
  googleRating: 5.0,
  googleReviewCount: 1,
  googleReviewsUrl: '',
  themeColor: '#000000',
  widgetClosedBgColor: '#000000',
  widgetClosedIconColor: '#ffffff',
  mainContactBtnBgColor: '#000000',
  mainContactBtnTextColor: '#ffffff',
  faqs: [],
  enableFaq: true, // Default enabled
  addresses: [],
  logoUrl: '',
  googleAnalyticsId: '',
  widgetAlignment: 'right',
  showBranding: true, // Default enabled

  // Tab Labels (Navigation)
  tabLabelHome: 'Home',
  tabLabelWelcome: 'Welcome',
  tabLabelContact: 'Contact',
  tabLabelCallus: 'Call Us',
  tabLabelHelp: 'Help',

  // UI Text Labels (Defaults)
  labelMainContactBtn: 'Contact us',
  labelQuickAnswers: 'Quick answers',
  labelShowAllQuestions: 'Show all questions',
  labelContactTitle: 'Contact us',
  labelLeaveMessageBtn: 'Leave a message',
  labelCallBackBtn: 'Request Call Back', // Default label
  labelCallUsBtn: 'Call Us',
  labelViberBtn: 'Viber Message',
  labelMessengerBtn: 'Messenger',
  labelVisitUsBtn: 'Visit Us',
  labelCallUsTitle: 'Call us',
  labelFaqTitle: 'FAQ',

  // Custom Home Button
  customHomeBtnLabel: '',
  customHomeBtnUrl: '',

  // Booking
  enableBooking: false,
  labelBookingBtn: 'Book Appointment',
  bookingBtnColor: '#ffffff',
  bookingBtnTextColor: '#1e293b',
  labelConsultation: 'Online Consultation',
  labelSession: 'Online Session',
  labelOnsite: 'Appointment On-site', // Default

  // Booking Logic Defaults
  appointmentDuration: 60,
  availableDays: [1, 2, 3, 4, 5], // Mon-Fri
  businessHoursStart: '09:00',
  businessHoursEnd: '17:00',
  businessHoursStartWeekend: '10:00',
  businessHoursEndWeekend: '15:00',

  // Booking Types Defaults
  enableConsultation: true,
  enableSession: true,
  enableOnsite: true,

  // Booking Custom Labels
  labelBookNowTitle: 'Book Now',
  labelSelectType: 'Select an appointment type:',
  descConsultation: 'Video call discussion',
  descSession: 'Full 1 hour session',
  descOnsite: 'Visit us at our office', // Default
  labelSelectDate: 'Select a date:',
  labelSelectTime: 'Select a time for',
  labelFormName: 'Your Name',
  labelFormEmail: 'Your Email',
  labelFormPhone: 'Your Phone',
  labelFormMessage: 'Message (Optional)', // Default
  labelConfirmBookingBtn: 'Confirm Booking',

  // Social Media Defaults
  socialFacebook: '',
  socialInstagram: '',
  socialYoutube: '',
  socialLinkedin: '',
  socialTiktok: '',
  //  enableDocumentNotes: false,
  enableAiAssistant: false, // @deprecated

  // New AI Architecture Defaults
  ai_features: {
    document_ai_enabled: false,
    auto_categorization_enabled: false
  },
  // Welcome Video Defaults
  enableWelcomeMessage: true, // Default enabled
  welcomeVideoType: 'upload',
  welcomeVideoUrl: '',
  welcomeImages: [], // Initialize array

  // Analytics Reports
  enableEmailReports: false,
  reportFrequency: 'weekly'
});
